import { ProcessingActivityFieldOrderItem } from "./ProcessingActivities.types";

// Mirror of Backend/Core/Filtering/ProcessingActivities/ProcessingActivityColumn.cs. Values are explicit and must
// stay in step: they travel in the search request, in sortByColumn and in the saved view blobs the backend serves.
export enum ProcessingActivityColumn {
    Id = 0,
    UserFriendlyId = 1,
    Name = 2,
    Status = 3,
    BusinessAreas = 4,
    Responsible = 5,
    SharedResponsibles = 6,
    GroupEntityCount = 7,
    SystemCount = 8,
    DataProcessorCount = 9,
    DisclosureCount = 10,
    SourceCount = 11,
    ApprovalNumber = 12,
    LastUpdated = 13,

    // Absorbed from the processing activity custom list.
    Description = 14,
    Purposes = 15,
    DataSubjects = 16,
    DataCategories = 17,
    DataCategoryCount = 18,
    Systems = 19,
    Policies = 20,
    SecurityMeasures = 21,
    NumberOfDataSubjects = 22,
    NumberOfProcessors = 23,
    CustomNumberOfDataSubjects = 24,
    CustomNumberOfProcessors = 25,
    GroupEntities = 26,
    LegalBases = 27,
    NationalLaws = 28,
    DeletionPeriods = 29,
    ValidationDate = 30,
    ValidatedBy = 31,
    DataSources = 32,
    Tags = 33,
    ReportApprovalDate = 34,
    ReportDataProcessorEndDate = 35,

    // Derived from the activity's sharings, the way the custom list derived them.
    SharingCount = 36,
    SenderCount = 37,
    RecipientCount = 38,
    RecipientsInEuCount = 39,
    RecipientsInEeaCount = 40,
    RecipientsWithAdequateProtectionCount = 41,
    RecipientsInInsecureThirdCountryCount = 42,
    TransferBases = 43,
    DataProcessors = 44,
    Disclosures = 45,
    Sources = 46,
    DataControllers = 47,
    DataControllerSharingCount = 48,
    JointDataControllers = 49,
    JointDataControllerSharingCount = 50,
    Documents = 51,
    DocumentTypes = 52,
    DocumentCount = 53,
    RiskAssessmentScore = 54,
    Progress = 55,
}

// The string ids the column picker, the table headers and user-saved views work in. Static like the legal entity
// ones: processing activities have no templates, so the vocabulary is known up front.
export const ProcessingActivityColumnId = {
    userFriendlyId: "userFriendlyId",
    name: "name",
    status: "status",
    businessAreas: "businessAreas",
    responsible: "responsible",
    sharedResponsibles: "sharedResponsibles",
    groupEntityCount: "groupEntityCount",
    systemCount: "systemCount",
    dataProcessorCount: "dataProcessorCount",
    disclosureCount: "disclosureCount",
    sourceCount: "sourceCount",
    approvalNumber: "approvalNumber",
    lastUpdated: "lastUpdated",
    description: "description",
    purposes: "purposes",
    dataSubjects: "dataSubjects",
    dataCategories: "dataCategories",
    dataCategoryCount: "dataCategoryCount",
    systems: "systems",
    policies: "policies",
    securityMeasures: "securityMeasures",
    numberOfDataSubjects: "numberOfDataSubjects",
    numberOfProcessors: "numberOfProcessors",
    customNumberOfDataSubjects: "customNumberOfDataSubjects",
    customNumberOfProcessors: "customNumberOfProcessors",
    groupEntities: "groupEntities",
    legalBases: "legalBases",
    nationalLaws: "nationalLaws",
    deletionPeriods: "deletionPeriods",
    validationDate: "validationDate",
    validatedBy: "validatedBy",
    dataSources: "dataSources",
    tags: "tags",
    reportApprovalDate: "reportApprovalDate",
    reportDataProcessorEndDate: "reportDataProcessorEndDate",
    sharingCount: "sharingCount",
    senderCount: "senderCount",
    recipientCount: "recipientCount",
    recipientsInEuCount: "recipientsInEuCount",
    recipientsInEeaCount: "recipientsInEeaCount",
    recipientsWithAdequateProtectionCount: "recipientsWithAdequateProtectionCount",
    recipientsInInsecureThirdCountryCount: "recipientsInInsecureThirdCountryCount",
    transferBases: "transferBases",
    dataProcessors: "dataProcessors",
    disclosures: "disclosures",
    sources: "sources",
    dataControllers: "dataControllers",
    dataControllerSharingCount: "dataControllerSharingCount",
    jointDataControllers: "jointDataControllers",
    jointDataControllerSharingCount: "jointDataControllerSharingCount",
    documents: "documents",
    documentTypes: "documentTypes",
    documentCount: "documentCount",
    riskAssessmentScore: "riskAssessmentScore",
    progress: "progress",
} as const;

// Derived from the ids above rather than written out a second time, so the two cannot fall out of step: each id
// is its enum member's name with a lower-case first letter.
export const processingActivityColumnByFilterId: Record<string, ProcessingActivityColumn> = Object.fromEntries(
    Object.entries(ProcessingActivityColumnId).map(([member, filterId]) => [
        filterId,
        ProcessingActivityColumn[(member.charAt(0).toUpperCase() + member.slice(1)) as keyof typeof ProcessingActivityColumn],
    ])
);

const processingActivityFilterIdByColumn: Record<number, string> = Object.fromEntries(
    Object.entries(processingActivityColumnByFilterId).map(([filterId, column]) => [column, filterId])
);
export function getProcessingActivityFilterIdByColumn(column: ProcessingActivityColumn): string | undefined {
    return processingActivityFilterIdByColumn[column];
}

// Order is the order the user arranged them in. An id the vocabulary no longer has is dropped rather than sent.
export function toProcessingActivityDisplayFields(filterIds: Array<string>): Array<ProcessingActivityFieldOrderItem> {
    return filterIds
        .map((filterId, index) => ({ column: processingActivityColumnByFilterId[filterId], displayOrder: index }))
        .filter((field) => field.column !== undefined);
}
